<div [formGroup]="formGroup">
  <!-- Section 1: Evaluation period and PI point interpolation -->
  <div class="border border-gray-200 rounded-xl p-4 m-4 mb-0">
    <p class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
      1. Période d'évaluation et interpolation PI
    </p>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label for="periodInMinutes" class="font-medium"
          >Période d'évaluation en minutes
          <span class="text-red-500">*</span></label
        >
        <p-inputnumber
          formControlName="periodInMinutes"
          [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
          class="w-full min-w-0 mt-2 relative"
          [showButtons]="true"
          [step]="0.1"
          placeholder="Ex: 4"
          [min]="10"
          [ngClass]="{ 'ng-invalid-border': getFieldError('periodInMinutes') }"
        ></p-inputnumber>
        <small
          *ngIf="getFieldError('periodInMinutes')"
          class="text-red-500 text-xs mt-1 block"
          >{{ getFieldError("periodInMinutes") }}</small
        >
      </div>

      <div>
        <label for="piPointInterpolationIntervalInMinutes" class="font-medium"
          >Interpolation (en minutes) des points PI</label
        >
        <p-inputnumber
          formControlName="piPointInterpolationIntervalInMinutes"
          [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
          class="w-full min-w-0 mt-2 relative"
          [showButtons]="true"
          [step]="1"
          placeholder="Ex: 60"
          [min]="1"
          [ngClass]="{
            'ng-invalid-border': getFieldError(
              'piPointInterpolationIntervalInMinutes'
            ),
          }"
        ></p-inputnumber>
        <small
          *ngIf="getFieldError('piPointInterpolationIntervalInMinutes')"
          class="text-red-500 text-xs mt-1 block"
          >{{ getFieldError("piPointInterpolationIntervalInMinutes") }}</small
        >
      </div>
    </div>
  </div>

  <!-- Section 2: Thresholds and nb of evaluations for raising an alarm -->
  <div class="border border-gray-200 rounded-xl p-4 m-4 mb-0">
    <p class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
      2. Seuils et nombre d'évaluations pour levée d'alerte
    </p>
    <app-thresholds [formGroup]="formGroup"></app-thresholds>
    <div class="mt-4">
      <label for="evaluationMode" class="font-medium"
        >Nb d'évaluations pour levée d'alerte
        <span class="text-red-500">*</span></label
      >
      <div class="flex items-center gap-3 mt-2">
        <p-select
          formControlName="evaluationMode"
          [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
          [options]="evaluationModeOptions"
          placeholder="Sélectionnez un mode"
          appendTo="body"
          styleClass="border-4 border-gray-200 rounded-xl focus:border-blue-500"
        ></p-select>

        <div class="flex items-center gap-2 flex-shrink-0">
          <p-inputnumber
            formControlName="minEvaluationsToTriggerAlarm"
            [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
            [readonly]="!showManualEvaluationFields"
            [showButtons]="showManualEvaluationFields"
            [step]="1"
            mode="decimal"
            [min]="1"
            [max]="9"
            [inputStyle]="{ width: '6rem' }"
            [class.opacity-75]="!showManualEvaluationFields"
          />
          <span class="font-bold text-xl">/</span>
          <p-inputnumber
            formControlName="evaluationsToConsiderForAlarm"
            [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
            [readonly]="!showManualEvaluationFields"
            [showButtons]="showManualEvaluationFields"
            [step]="1"
            mode="decimal"
            [min]="1"
            [max]="9"
            [inputStyle]="{ width: '6rem' }"
            [class.opacity-75]="!showManualEvaluationFields"
          />
        </div>
      </div>
      <small
        *ngIf="getFieldError('minEvaluationsToTriggerAlarm')"
        class="text-red-500 text-xs mt-1 block"
        >{{ getFieldError("minEvaluationsToTriggerAlarm") }}</small
      >
      <small
        *ngIf="getFieldError('evaluationsToConsiderForAlarm')"
        class="text-red-500 text-xs mt-1 block"
        >{{ getFieldError("evaluationsToConsiderForAlarm") }}</small
      >
    </div>
  </div>

  <!-- Section 3: Python code and graphs -->
  <div class="border border-gray-200 rounded-xl p-4 m-4">
    <p class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
      3. Code Python et graphiques
    </p>

    <div class="flex items-center gap-3 mb-4">
      <span class="text-sm font-medium text-gray-700">Mode Agent</span>
      <div
        [pTooltip]="
          !isAgentModeAvailable
            ? mode === 'edit'
              ? 'L\'agent n\'est pas disponible pour cette règle'
              : 'Veuillez sélectionner un Poste Technique dans la section Localisation'
            : ''
        "
        [tooltipDisabled]="isAgentModeAvailable"
        tooltipPosition="top"
      >
        <p-toggleSwitch
          [(ngModel)]="agentMode"
          [ngModelOptions]="{ standalone: true }"
          [disabled]="!isAgentModeAvailable"
        ></p-toggleSwitch>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        class="grid grid-cols-1 md:grid-cols-2 col-span-1 md:col-span-2 gap-4 w-full items-start"
      >
        <!-- Left column: code editors or agent chat -->
        <div class="flex flex-col gap-4">
          <!-- Agent mode -->
          <div
            *ngIf="agentMode"
            class="flex flex-col"
            style="height: 750px; max-height: 750px"
          >
            <app-rule-creation-chat
              class="flex flex-col"
              style="height: 100%; min-height: 0"
              [equipmentId]="selectedEquipmentId"
              [mode]="mode"
              [ruleId]="selectedRuleId"
              (evaluationConfigChange)="onAgentEvaluationConfigChange($event)"
              (validatedPiPointsChange)="onAgentValidatedPiPointsChange($event)"
              (monitoringEquationChange)="
                onAgentMonitoringEquationChange($event)
              "
              (activationConditionChange)="
                onAgentActivationConditionChange($event)
              "
              (validationChange)="onAgentValidationComplete($event)"
            ></app-rule-creation-chat>
          </div>

          <!-- Manual mode -->
          <ng-container *ngIf="!agentMode">
            <div>
              <label for="activationConditionPiExpression" class="font-medium">
                Condition d'activation
              </label>
              <app-live-code-display
                [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
                [editable]="true"
                componentId="activationConditionPythonExpression"
                [validateSyntax]="true"
                [minHeight]="'250px'"
                [placeholder]="'Entrez votre code Python pour la règle...'"
                [isLoadingExistingPiPoints]="
                  (isLoadingPiPointsByRule$ | async) || false
                "
                formControlName="activationConditionPythonExpression"
                (syntaxValidation)="
                  onActivationConditionSyntaxValidation($event)
                "
                (piPointValidation)="
                  onActivationConditionPiPointValidation($event)
                "
                (piPointValidationLoading)="
                  onActivationConditionPiPointValidationLoading($event)
                "
              ></app-live-code-display>
            </div>
            <div>
              <label for="pythonCode" class="font-medium"
                >Code Python <span class="text-red-500">*</span></label
              >
              <app-live-code-display
                [editable]="true"
                [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
                componentId="pythonCode"
                [validateSyntax]="true"
                [enablePiPointValidation]="false"
                [minHeight]="'250px'"
                [placeholder]="'Entrez votre code Python pour la règle...'"
                [isLoadingExistingPiPoints]="
                  (isLoadingPiPointsByRule$ | async) || false
                "
                formControlName="pythonCode"
                (syntaxValidation)="onPythonCodeSyntaxValidation($event)"
                (piPointValidation)="onPythonCodePiPointValidation($event)"
                (piPointValidationLoading)="
                  onPythonCodePiPointValidationLoading($event)
                "
              ></app-live-code-display>
              <small
                *ngIf="getFieldError('pythonCode')"
                class="text-red-500 text-xs mt-1 block"
                >{{ getFieldError("pythonCode") }}</small
              >
            </div>
          </ng-container>
        </div>

        <!-- Right column: charts (always visible) -->
        <div class="flex flex-col">
          <div
            class="flex items-center justify-center border border-gray-100 rounded-xl mb-4 mt-10"
          >
            <div class="card w-full h-full pb-2">
              <span class="text-xl font-semibold mb-2">Paramètres Mesurés</span>
              <ng-template #loadingTpl>
                <div class="flex justify-center">
                  <p-progress-spinner ariaLabel="loading" />
                </div>
              </ng-template>
              <div class="relative">
                <div
                  *ngIf="loadingPiData"
                  class="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg"
                >
                  <p-progress-spinner
                    ariaLabel="loading"
                    [style]="{ width: '40px', height: '40px' }"
                  />
                </div>
                <div>
                  <app-interactive-time-chart
                    [datasets]="interactivePiChartDatasets"
                    [height]="250"
                    [chartOptions]="piPointsChartOptions"
                    [displayonReset]="false"
                  ></app-interactive-time-chart>
                </div>
              </div>
            </div>
          </div>
          <div class="flex items-center justify-between w-full mb-2">
            <div class="flex items-center flex-grow">
              <label class="text-base flex-shrink-0">Date de début : </label>
              <p-datepicker
                formControlName="evaluationStartDate"
                placeholder="Date de début de l'évaluation"
                size="small"
                showIcon
                iconDisplay="input"
                class="text-sm flex-grow"
                styleClass="text-sm p-inputtext-sm w-full"
              ></p-datepicker>
            </div>
            <div class="flex items-center flex-grow">
              <label class="text-base flex-shrink-0">Date de fin : </label>
              <p-datepicker
                formControlName="evaluationEndDate"
                placeholder="Date de fin"
                dateFormat="dd/mm/yy"
                size="small"
                showIcon
                iconDisplay="input"
                class="text-sm flex-grow"
                styleClass="text-sm p-inputtext-sm w-full"
              ></p-datepicker>
            </div>
            <button
              pButton
              size="small"
              severity="success"
              [disabled]="!canEvaluateRule() || evaluationLoading"
              [pTooltip]="getEvaluationTooltip()"
              [tooltipDisabled]="canEvaluateRule()"
              tooltipPosition="top"
              tooltipStyleClass="validation-tooltip"
              (click)="onEvaluate()"
            >
              <i
                *ngIf="!evaluationLoading; else loadingIcon"
                class="pi pi-play-circle ml-2"
              ></i>
              <ng-template #loadingIcon>
                <i class="pi pi-spin pi-spinner ml-2"></i>
              </ng-template>
              <span class="font-medium mr-3">Evaluation</span>
            </button>
          </div>
          <small
            *ngIf="getFieldError('evaluationStartDate')"
            class="text-red-500 text-xs mb-1 block"
            >Date début: {{ getFieldError("evaluationStartDate") }}</small
          >
          <small
            *ngIf="getFieldError('evaluationEndDate')"
            class="text-red-500 text-xs mb-1 block"
            >Date fin: {{ getFieldError("evaluationEndDate") }}</small
          >
          <small
            *ngIf="largePeriodWarning"
            class="text-orange-500 text-xs mb-1 block"
            ><i class="pi pi-exclamation-triangle mr-1"></i>La période
            sélectionnée dépasse un an. Veuillez réduire la plage de
            dates.</small
          >
          <div
            class="flex-1 flex items-center justify-center border border-gray-100 rounded-xl h-full"
          >
            <div class="flex-1 card w-full h-full pb-2">
              <span class="text-xl font-semibold mb-2"
                >Tendance de Déviation du Modèle</span
              >
              <ng-container
                *ngIf="
                  evaluationError && evaluationErrorDetail;
                  else chartDisplay
                "
              >
                <div
                  class="bg-red-50 border-2 border-red-300 rounded-lg p-4 mt-2"
                >
                  <div
                    [innerHTML]="evaluationErrorDetail"
                    class="text-red-500 p-2"
                  ></div>
                </div>
              </ng-container>
              <ng-template #chartDisplay>
                <app-data-state
                  [loading]="evaluationLoading"
                  [error]="evaluationError"
                  [height]="286"
                >
                  <app-interactive-time-chart
                    [datasets]="interactiveChartDatasets"
                    [height]="250"
                    [chartOptions]="ruleEvaluationChartOptions"
                    [displayonReset]="false"
                  ></app-interactive-time-chart>
                </app-data-state>
              </ng-template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
